import { Link } from "wouter";
import { Compass, User, Settings, Crown, Briefcase, ShieldCheck } from "lucide-react";
import { PageHeading, Section, Steps, RoleBadge, pathFor, Callout, Diagram, MockButton, MockRow } from "../shared";

export default function QuickStartSection() {
  return (
    <div data-testid="guide-quick-start">
      <PageHeading
        icon={Compass}
        title="Quick Start by Role"
        description="The fastest way to get going depends on your job. Find the heading that sounds most like you and follow the short walkthrough — each step links to a fuller guide if you want more detail."
      />

      <Callout variant="info" title="Not sure which one is you?">
        Most everyday users are a <span className="text-white">Member</span> (you do work and add updates) or a <span className="text-white">General Admin</span> (you create and manage items). The other roles are for senior reviewers and people who configure the system.
      </Callout>

      <Diagram
        title="Where to start, by role"
        frameLabel="Pick the heading that sounds like you"
        legend={[
          <><span className="text-white">Member</span> — start on the <span className="text-white">Summary</span> or <span className="text-white">Updates</span> page to see and update your work.</>,
          <><span className="text-white">General Admin</span> — start on <span className="text-white">Register</span> to create items, then <span className="text-white">Updates</span> to manage them.</>,
          <><span className="text-white">SDF MD / MD Admin</span> — start on <span className="text-white">MD Meetings</span> to clear the review queue.</>,
          <><span className="text-white">PMO / Board Secretary</span> — start on <span className="text-white">MD Meetings → Awaiting Dispatch</span> to send approved items upward.</>,
          <><span className="text-white">Super Admin</span> — start on <span className="text-white">Admin → Versions</span> to set up the rules.</>,
        ]}
      >
        <MockRow>
          <MockButton label="Summary / Updates" marker={1} variant="ghost" />
          <MockButton label="Register" marker={2} variant="ghost" />
          <MockButton label="MD Meetings" marker={3} variant="ghost" />
          <MockButton label="Awaiting Dispatch" marker={4} variant="ghost" />
          <MockButton label="Admin → Versions" marker={5} variant="ghost" />
        </MockRow>
      </Diagram>

      <Section title="I am a Member">
        <div className="flex items-center gap-2 mb-3">
          <User className="w-4 h-4 text-slate-300" />
          <RoleBadge role="member" />
        </div>
        <p className="text-sm text-white/65 leading-relaxed">
          You can see the action items assigned to you (or that you're currently holding, or that you have a sub-task on) and add progress notes. You can't create new items or change their status.
        </p>
        <Steps
          steps={[
            <>Open the <span className="text-white">Summary</span> page (the home page) — your items appear under their authority lane.</>,
            <>Or go to <span className="text-white">Updates</span> for the day-to-day view — your items, with the original instruction shown.</>,
            <>Click any item to open it, then add a progress note describing what you've done. The item's owner is emailed automatically.</>,
            <>If you see an <span className="text-orange-300">orange "Returned for Amendments"</span> banner, the reviewer wants changes — read their note and update accordingly.</>,
            <>If you have sub-tasks, they show on the Updates page with their own status — mark them complete when done.</>,
          ]}
        />
      </Section>

      <Section title="I am a General Admin (day-to-day operations)">
        <div className="flex items-center gap-2 mb-3">
          <Settings className="w-4 h-4 text-green-300" />
          <RoleBadge role="admin" />
        </div>
        <p className="text-sm text-white/65 leading-relaxed">
          You handle the full life of an action item — creating it, moving it through its statuses, handing it to other people, and closing it.
        </p>
        <Steps
          steps={[
            <><Link href={pathFor("forms/register")} className="text-cyan-300 hover:underline">Register a new action item</Link> from the Register page (or the green button in the header).</>,
            <>Manage progress on the <span className="text-white">Updates</span> page — change statuses, hand off responsibility, and add notes.</>,
            <>When an item needs review, set its status to a review state (e.g. <span className="text-purple-300">With SDF MD Review</span>).</>,
            <>Once reviewed, close it with a <Link href={pathFor("forms/approve-close")} className="text-cyan-300 hover:underline">closing note</Link> (sign-off is checked for you automatically).</>,
            <>Use <span className="text-white">Admin → KPI Dashboard</span> for performance numbers and Excel reports, and the <Link href={pathFor("pages/hr-dossier")} className="text-cyan-300 hover:underline">HR Dossier</Link> for people and team reports.</>,
          ]}
        />
      </Section>

      <Section title="I am the SDF MD (or MD Admin reviewing the queue)">
        <div className="flex items-center gap-2 mb-3">
          <Crown className="w-4 h-4 text-purple-300" />
          <RoleBadge role="md_admin" />
        </div>
        <p className="text-sm text-white/65 leading-relaxed">
          The MD Meetings page is your queue for everything currently waiting on SDF MD review.
        </p>
        <Steps
          steps={[
            <>Open <span className="text-white">MD Meetings</span> from the header — the Review Queue tab shows every item <span className="text-purple-300">With SDF MD Review</span>.</>,
            <>Filter by due bucket, priority, or "show only mine" if you're bound to the SDF MD team member.</>,
            <>Tick the items you want to action, then use <Link href={pathFor("forms/approve-close")} className="text-cyan-300 hover:underline">Approve &amp; Close</Link> to clear them in one go — sign-off is recorded automatically when required.</>,
            <>If an item needs rework, use the row's <Link href={pathFor("forms/return")} className="text-cyan-300 hover:underline">Return</Link> action with a clear reason — it goes back to the original owner.</>,
            <>Switch to the <span className="text-yellow-300">Awaiting Dispatch</span> tab to see items you've approved that PMO still needs to send upward.</>,
            <>Click the header button to download a landscape PDF of the queue as today's meeting agenda.</>,
          ]}
        />
        <Callout variant="info">
          MD Admin (read-only) can view the queue and download the PDF. Only the actual SDF MD team member or a Super Admin can approve, sign off, return, or mark dispatched.
        </Callout>
      </Section>

      <Section title="I am the PMO or Board Secretary (sending items upward)">
        <div className="flex items-center gap-2 mb-3">
          <Briefcase className="w-4 h-4 text-amber-300" />
          <RoleBadge role="admin" />
        </div>
        <p className="text-sm text-white/65 leading-relaxed">
          When the SDF MD approves an above-MD item (EDGE Chairman, EDGE MD Office, Board / ExCom), it lands in <span className="text-yellow-300">Awaiting Dispatch</span>. You have <span className="text-white">two working days</span> (the default target) to mark it sent.
        </p>
        <Steps
          steps={[
            <>Open <span className="text-white">MD Meetings → Awaiting Dispatch</span>. Items are sorted oldest-approved-first.</>,
            <>Watch the held-time chip — <span className="text-emerald-300">green</span> is within target, <span className="text-amber-300">amber</span> is +1–2 days, <span className="text-red-300">red</span> is beyond.</>,
            <>Click <Link href={pathFor("forms/mark-dispatched")} className="text-cyan-300 hover:underline">Mark Dispatched</Link> on the row, fill in the recipient, the date you actually sent it, and an optional note.</>,
            <>The item moves to <span className="text-indigo-300">Sent Upward</span>, the SDF MD is notified, and your dispatch time is recorded for the KPI panel.</>,
          ]}
        />
      </Section>

      <Section title="I am a Super Admin (setting up the rules)">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-4 h-4 text-purple-300" />
          <RoleBadge role="super_admin" />
        </div>
        <p className="text-sm text-white/65 leading-relaxed">
          You configure how the system behaves — lanes, actors, reference lists, workflow gates, and field permissions. Changes are made to a draft version first, then switched on.
        </p>
        <Steps
          steps={[
            <>Open <span className="text-white">Admin → Versions</span>, click <span className="text-white">"Clone Active to New Draft"</span>, and give the draft a name and effective date.</>,
            <>Use the version selector at the top of the Admin page to focus on the draft, then edit Lanes / Actors / Reference Lists / Workflow Rules / Field Permissions / Transition Permissions.</>,
            <>When the draft is ready, click <span className="text-white">Activate</span>. The previously active version is retired for you.</>,
            <>Activated versions are read-only — to change anything else, clone again.</>,
          ]}
        />
        <Callout variant="warning">
          IT-level settings (SMTP email, branding, backups, agent API keys) live on the separate <span className="text-white">Settings</span> page, not Admin.
        </Callout>
      </Section>
    </div>
  );
}
